import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Link,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import roleAdminService from '../services/roleAdminService';
import useCan from '../hooks/useCan';
import { useOptionalPermissions } from '../context/permissionContextCore';
import PermissionGrid from '../components/roles/PermissionGrid';
import RoleUsersPanel from '../components/roles/RoleUsersPanel';

// §10.3 protegge la pagina con roles.read; scrivere richiede roles.update. Il dettaglio si
// apriva completo a chi ha il solo roles.read -- caselle cliccabili, «Salva modifiche» che si
// accende -- e il 403 arrivava al click, dopo che il ruolo era stato ridisegnato. Stessa
// forma di `UserRolesPanel`, che questa correzione ha semplicemente allineato.
const SPIEGAZIONE_SENZA_PERMESSO =
    'Modificare i permessi di un ruolo richiede il permesso roles.update, che manca: la ' +
    'griglia è in sola lettura.';

// Il perché è detto in termini d'uso e non di implementazione. La stesura precedente
// parlava dello script che genera il seed, del test che legge il changelog e del «test
// verde»: la motivazione interna della scelta, in un banner rivolto a chi configura un
// ruolo e non può verificare nessuna di quelle tre cose. «Identici in tutti gli ambienti»
// è la stessa sostanza — è il motivo per cui una modifica da interfaccia sarebbe un
// problema — detta in modo che a un amministratore serva: se lo cambio qui, questo ruolo
// non è più lo stesso che c'è in produzione. Il dettaglio implementativo resta nel Javadoc
// del componente, che è il posto per cui è scritto.
const SPIEGAZIONE_SOLA_LETTURA =
    'I ruoli di sistema non si modificano da qui: sono predefiniti e identici in tutti ' +
    'gli ambienti. Per ottenere una variante con permessi diversi, duplicalo dal tab Ruoli.';

// Senza il numero del paragrafo di specifica: a schermo rimandava a un documento che chi
// configura un ruolo non ha aperto, e sembrava una nota rimasta dentro per sbaglio.
const NOTA_STRUMENTI =
    'Non c\'è un elenco separato di strumenti da abilitare: ogni strumento segue il permesso ' +
    'che lo rende possibile — scaricare un file richiede «File allegati · Consulta», ' +
    'aggiornare il sistema richiede «Impostazioni · Modifica».';

// Fase F6: il tab «Code» e' sparito con role_queue -- le code appartengono al gruppo, non
// al ruolo. Chi arriva qui da un segnalibro (`?tab=code`, o il vecchio indice 1) non deve
// trovare un tab vuoto ne' una pagina rotta: si apre su Permessi e si dice dove le code sono
// andate. Un rimando esplicito costa una riga e risparmia la ricerca a chi non ha seguito la
// migrazione.
const RIMANDO_CODE =
    'Le code non appartengono più al ruolo: si configurano dalla pagina Gruppi, insieme ' +
    'ai tipi di Codex. Chi vede una coda è chi ha un ruolo dentro un gruppo che la contiene.';

function formattaData(valore) {
    return valore ? new Date(valore).toLocaleDateString('it-IT') : '—';
}

/**
 * L'insieme dei permessi concessi coincide con quello caricato? Il confronto e' per
 * contenuto — codice e ambito, non ordine — non per un flag `dirty` messo a mano: una
 * bandiera resta accesa anche quando l'utente rimette a posto cio' che aveva cambiato, e
 * «Salva modifiche» attivo senza modifiche pendenti e' una promessa falsa (§10.3).
 */
function stessoInsieme(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    const chiave = (g) => `${g.permissionCode}::${g.scope || ''}`;
    const ordinaA = [...a].map(chiave).sort();
    const ordinaB = [...b].map(chiave).sort();
    return ordinaA.every((v, i) => v === ordinaB[i]);
}

/**
 * Dettaglio di un ruolo (spec §10.3): intestazione, tab Permessi (con `PermissionGrid`) e
 * tab Utenti assegnati (con `RoleUsersPanel`).
 *
 * La griglia e' in sola lettura in due casi distinti, che la pagina distingue anche nella
 * spiegazione: sui **ruoli di sistema**, il cui contenuto e' generato da uno script, e a chi
 * **non ha roles.update**, che il backend rifiuterebbe con 403. Sui ruoli di sistema la
 * griglia e' in sola lettura e i controlli di salvataggio non
 * compaiono affatto — non solo disabilitati: mostrare un pulsante che il backend
 * rifiuterebbe con 409 e' il difetto che questa fase esiste per eliminare (vedi il
 * Javadoc di `RolesTab`). La ragione della sola lettura si legge in pagina, non solo nel
 * divieto.
 */
export default function RoleDetail() {
    const can = useCan();
    // Chi modifica i permessi di un ruolo puo' esserne titolare: si rileggono i propri.
    const refreshPermissions = useOptionalPermissions()?.refresh;
    const { id } = useParams();

    const canUpdate = can('roles.update');
    // I due motivi non si sommano in un booleano solo: la spiegazione a schermo e' diversa,
    // e su un ruolo di sistema vale quella del ruolo -- e' la ragione piu' specifica.

    const [role, setRole] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [savedGranted, setSavedGranted] = useState([]);
    const [granted, setGranted] = useState([]);
    const [otherRoles, setOtherRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [tab, setTab] = useState(0);
    // Il segnalibro del tab sparito: `?tab=code` (e il vecchio `?tab=1`) non aprono piu'
    // niente, ma spiegano dove le code sono andate invece di lasciare la pagina muta.
    const [searchParams] = useSearchParams();
    const rimandoCode = ['code', 'queues', '1'].includes(
        (searchParams.get('tab') || '').toLowerCase(),
    );

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        setSaveError(null);
        Promise.all([
            roleAdminService.getRole(id),
            roleAdminService.listPermissionCatalog(),
            roleAdminService.listRoles(),
        ])
            .then(([roleData, catalogData, rolesData]) => {
                setRole(roleData);
                setCatalog(catalogData || []);
                const grantedData = roleData.permissions || [];
                setSavedGranted(grantedData);
                setGranted(grantedData);

                // otherRoles alimenta «Copia da un altro ruolo» in PermissionGrid: serve
                // l'insieme dei permessi di ciascun altro ruolo, che `listRoles()` non
                // porta (solo il conteggio) — va letto dal dettaglio di ciascuno.
                const altri = (rolesData || []).filter((r) => r.id !== id);
                return Promise.all(
                    altri.map((r) =>
                        roleAdminService.getRole(r.id).then(
                            (dettaglio) => ({
                                id: r.id,
                                label: r.label,
                                granted: dettaglio.permissions || [],
                            }),
                            () => null,
                        ),
                    ),
                );
            })
            .then((lista) => {
                setOtherRoles((lista || []).filter(Boolean));
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const isDirty = useMemo(() => !stessoInsieme(granted, savedGranted), [granted, savedGranted]);

    const handleCancel = () => {
        setGranted(savedGranted);
        setSaveError(null);
    };

    const handleSave = () => {
        setSaving(true);
        setSaveError(null);
        roleAdminService.replacePermissions(id, granted).then(
            (updated) => {
                setSaving(false);
                setRole(updated);
                const grantedData = updated.permissions || [];
                setSavedGranted(grantedData);
                setGranted(grantedData);
                refreshPermissions?.();
            },
            (err) => {
                setSaving(false);
                // Il rifiuto dell'invariante (es. «ultimo amministratore», 409) deve
                // raggiungere l'utente col messaggio del backend: inghiottirlo lo
                // lascerebbe credere di aver salvato.
                setSaveError(
                    err?.response?.data?.message ||
                        'Non è stato possibile salvare i permessi.',
                );
            },
        );
    };

    if (loading) {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    Caricamento…
                </Typography>
            </Box>
        );
    }

    if (error || !role) {
        return (
            <Box>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Riprova
                        </Button>
                    }
                >
                    Non è stato possibile caricare il ruolo.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/roles-and-permissions">
                    Ruoli e permessi
                </Link>
                <Typography color="text.primary">{role.label}</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4">{role.label}</Typography>
                    <Chip
                        size="small"
                        label={role.systemRole ? 'Di sistema' : 'Personalizzato'}
                    />
                    <Chip
                        size="small"
                        label={`${role.userCount} ${role.userCount === 1 ? 'utente assegnato' : 'utenti assegnati'}`}
                    />
                </Box>
                {/* Le azioni salgono accanto al titolo (§10.3): sui ruoli di sistema non
                    compaiono affatto, per lo stesso motivo per cui la griglia e' in sola
                    lettura — mostrare un pulsante che il backend rifiuterebbe e' il difetto
                    che questa fase elimina. La logica di abilitazione (isDirty) non cambia,
                    solo la posizione. */}
                {tab === 0 && !role.systemRole && canUpdate && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancel} disabled={!isDirty || saving}>
                            Annulla
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!isDirty || saving}
                        >
                            Salva modifiche
                        </Button>
                    </Box>
                )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {role.description}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Creato il {formattaData(role.createdAt)}
                {role.createdByName ? ` da ${role.createdByName}` : ''}
                {' · ultima modifica '}
                {formattaData(role.updatedAt)}
                {role.updatedByName ? ` da ${role.updatedByName}` : ''}
            </Typography>

            {rimandoCode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {RIMANDO_CODE}{' '}
                    <Link component={RouterLink} to="/groups">
                        Vai ai Gruppi
                    </Link>
                </Alert>
            )}

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Permessi" />
                <Tab label="Utenti assegnati" />
            </Tabs>

            {tab === 0 && (
                <Box>
                    {role.systemRole && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {SPIEGAZIONE_SOLA_LETTURA}
                        </Alert>
                    )}
                    {!role.systemRole && !canUpdate && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {SPIEGAZIONE_SENZA_PERMESSO}
                        </Alert>
                    )}
                    {saveError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                            {saveError}
                        </Alert>
                    )}
                    <PermissionGrid
                        catalog={catalog}
                        granted={granted}
                        readOnly={role.systemRole || !canUpdate}
                        onChange={setGranted}
                        otherRoles={otherRoles}
                    />
                </Box>
            )}

            {tab === 1 && <RoleUsersPanel roleId={id} />}

            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 3 }}>
                {NOTA_STRUMENTI}
            </Typography>
        </Box>
    );
}
